import { useRef } from "react";
import { useDebounceWithCancel } from "@certa/common/hooks/useDebounce";

const SCROLL_DEBOUNCE_MS = 100;
const SCROLL_THRESHOLD_PX = 500;

/**
 * Hook to handle infinite scrolling functionality
 * @param onScrollEnd - Callback function to be called when user scrolls near the bottom
 * @returns Object containing scroll handler and ref to be attached to scrollable container
 */
export const useInfiniteScroll = (onScrollEnd?: () => void) => {
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);

  const debouncedScroll = useDebounceWithCancel((element: HTMLDivElement) => {
    if (!onScrollEnd) return;

    const { scrollHeight, scrollTop, clientHeight } = element;

    // Check if scrolling downwards
    const isScrollingDown = scrollTop > lastScrollTop.current;
    lastScrollTop.current = scrollTop;

    // Only fetch more if scrolling down and near the bottom
    if (
      isScrollingDown &&
      scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX
    ) {
      onScrollEnd();
    }
  }, SCROLL_DEBOUNCE_MS);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    debouncedScroll(e.currentTarget);
  };

  return {
    scrollableContainerRef,
    handleScroll
  };
};
